import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, MessageCircle, CheckCircle2, UserPlus } from "lucide-react";
import { FadeUp, SectionHeader } from "./Reveal";
import { CONTACT, COURSES, LANGUAGES } from "../../lib/content";
import { detectRegion, fmtMoney, STATIC_PRICES, batchPrice, batchDropinPrice } from "../../lib/pricing";
import { ShareCard } from "./ShareCard";
import { useSound } from "../../hooks/useSound";
import { useStudentAuth } from "../../context/StudentAuth";

const API = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT}"]`);
    if (existing) { existing.addEventListener("load", () => resolve(true)); existing.addEventListener("error", () => resolve(false)); return; }
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const fmtDate = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

const COUNTRY_CODES = [
  { c: "IN", name: "India", dial: "+91", flag: "🇮🇳" },
  { c: "US", name: "United States", dial: "+1", flag: "🇺🇸" },
  { c: "GB", name: "United Kingdom", dial: "+44", flag: "🇬🇧" },
  { c: "AE", name: "UAE", dial: "+971", flag: "🇦🇪" },
  { c: "CA", name: "Canada", dial: "+1", flag: "🇨🇦" },
  { c: "AU", name: "Australia", dial: "+61", flag: "🇦🇺" },
  { c: "SG", name: "Singapore", dial: "+65", flag: "🇸🇬" },
  { c: "DE", name: "Germany", dial: "+49", flag: "🇩🇪" },
  { c: "FR", name: "France", dial: "+33", flag: "🇫🇷" },
  { c: "MY", name: "Malaysia", dial: "+60", flag: "🇲🇾" },
  { c: "SA", name: "Saudi Arabia", dial: "+966", flag: "🇸🇦" },
  { c: "QA", name: "Qatar", dial: "+974", flag: "🇶🇦" },
  { c: "OM", name: "Oman", dial: "+968", flag: "🇴🇲" },
  { c: "NZ", name: "New Zealand", dial: "+64", flag: "🇳🇿" },
  { c: "JP", name: "Japan", dial: "+81", flag: "🇯🇵" },
];

const EMAIL_DOMAINS = ["gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "icloud.com"];
const TYPO_MAP = { gamil: "gmail", gmial: "gmail", gmali: "gmail", gmil: "gmail", yaho: "yahoo", yahooo: "yahoo", hotmial: "hotmail", hotmal: "hotmail", outlok: "outlook", iclod: "icloud" };

const emailHints = (email) => {
  const at = email.indexOf("@");
  if (at < 1) return [];
  const local = email.slice(0, at);
  const domain = email.slice(at + 1).toLowerCase();
  if (EMAIL_DOMAINS.includes(domain)) return [];
  const typoKey = Object.keys(TYPO_MAP).find((t) => domain.startsWith(t));
  if (typoKey) return [`${local}@${domain.replace(typoKey, TYPO_MAP[typoKey])}`];
  const matches = EMAIL_DOMAINS.filter((d) => d.startsWith(domain));
  if (matches.length) return matches.slice(0, 4).map((d) => `${local}@${d}`);
  if (!domain.includes(".")) return EMAIL_DOMAINS.slice(0, 3).map((d) => `${local}@${d}`);
  return [];
};

const Chip = ({ selected, onClick, children, testId, className = "" }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`text-left px-4 py-3 border font-mono text-[11px] transition-colors duration-300 ${
      selected ? "border-[#DB0909] bg-[#210101] text-[#f7f6f2]" : "border-[rgba(28,22,16,0.18)] text-[#4a3e30] hover:border-[#DB0909]/60 hover:text-[#1c1610]"
    } ${className}`}
  >
    {children}
  </button>
);

export const Contact = () => {
  const { pluck } = useSound();
  const { user: studentUser } = useStudentAuth();
  const [batches, setBatches] = useState([]);
  const [course, setCourse] = useState("songs");
  const region = detectRegion();
  const [batchId, setBatchId] = useState(null);
  const [type, setType] = useState("full");
  const [classes, setClasses] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });

  // Prefill from student auth if logged in
  useEffect(() => {
    if (studentUser && !form.name && !form.email) {
      setForm((f) => ({ ...f, name: studentUser.name || "", email: studentUser.email || "" }));
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentUser]);
  const [dial, setDial] = useState("+91");
  const [otp, setOtp] = useState({ open: false, code: "", sent: false, verified: false, sending: false, verifying: false, cooldown: 0, msg: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  // sync dial code with detected region on first render
  useEffect(() => { setDial(region === "in" ? "+91" : "+1"); }, [region]);

  // cooldown countdown
  useEffect(() => {
    if (!otp.cooldown) return;
    const t = setTimeout(() => setOtp((o) => ({ ...o, cooldown: o.cooldown - 1 })), 1000);
    return () => clearTimeout(t);
  }, [otp.cooldown]);

  // if email changes, verification is invalidated
  useEffect(() => { setOtp((o) => (o.verified ? { ...o, verified: false, sent: false, code: "", msg: "" } : o)); }, [form.email]);

  // listen for course-preselect events from the vinyl "Reserve Side" buttons
  useEffect(() => {
    const onPreselect = (e) => {
      if (e.detail && (e.detail === "songs" || e.detail === "indie")) setCourse(e.detail);
    };
    window.addEventListener("preselect-course", onPreselect);
    return () => window.removeEventListener("preselect-course", onPreselect);
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/batches`).then((r) => setBatches(r.data)).catch(() => {});
  }, []);

  const resetBooking = () => {
    setResult(null);
    setError("");
    setForm({ name: "", phone: "", email: "" });
    setOtp({ open: false, code: "", sent: false, verified: false, sending: false, verifying: false, cooldown: 0, msg: "" });
    setClasses([]);
    setType("full");
    axios.get(`${API}/api/batches`).then((r) => setBatches(r.data)).catch(() => {});
  };

  const courseBatches = useMemo(() => batches.filter((b) => b.course_id === course && b.spots_left > 0), [batches, course]);
  const batch = courseBatches.find((b) => b.id === batchId) || courseBatches[0] || null;

  useEffect(() => {
    setBatchId(courseBatches[0]?.id || null);
    setType("full");
    setClasses([]);
  }, [course]); // eslint-disable-line react-hooks/exhaustive-deps

  const effType = batch?.status === "in_progress" ? "dropin" : type;
  const remaining = batch ? batch.sessions.map((s, i) => ({ s, i })).slice(batch.classes_completed) : [];
  const amount = !batch ? 0 : effType === "dropin" ? classes.length * batchDropinPrice(batch, region) : batchPrice(batch, region);

  const emailValid = /.+@.+\..+/.test(form.email);

  const sendOtp = async () => {
    if (!emailValid) return setOtp((o) => ({ ...o, msg: "Enter a valid email first" }));
    setOtp((o) => ({ ...o, sending: true, msg: "" }));
    try {
      const { data } = await axios.post(`${API}/api/otp/email/send`, { email: form.email.trim().toLowerCase() });
      setOtp((o) => ({ ...o, open: true, sent: true, sending: false, cooldown: data.cooldown || 45, msg: "Code sent — check your inbox (and spam)" }));
    } catch (e) {
      const d = e.response?.data?.detail;
      setOtp((o) => ({ ...o, sending: false, msg: typeof d === "string" ? d : "Could not send code — try again" }));
    }
  };

  const verifyOtp = async () => {
    if (!/^\d{4,8}$/.test(otp.code)) return setOtp((o) => ({ ...o, msg: "Enter the 6-digit code" }));
    setOtp((o) => ({ ...o, verifying: true, msg: "" }));
    try {
      await axios.post(`${API}/api/otp/email/verify`, { email: form.email.trim().toLowerCase(), code: otp.code });
      setOtp((o) => ({ ...o, verified: true, verifying: false, open: false, msg: "" }));
    } catch (e) {
      const d = e.response?.data?.detail;
      setOtp((o) => ({ ...o, verifying: false, msg: typeof d === "string" ? d : "Verification failed" }));
    }
  };

  const submit = async () => {
    setError("");
    if (!batch) return setError("No batch available — reach out on WhatsApp.");
    if (form.name.trim().length < 2 || !emailValid) return setError("Please add your name and a valid email.");
    if (effType === "dropin" && classes.length === 0) return setError("Pick at least one class for drop-in.");
    // Logged-in students bypass the OTP step — the email is already verified by their session
    if (!studentUser && !otp.verified) {
      // gate: send OTP first (if not sent yet) then reveal input
      if (!otp.sent) await sendOtp();
      else setOtp((o) => ({ ...o, open: true }));
      return;
    }
    setSubmitting(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load payment gateway. Check your internet and try again.");

      const combinedPhone = form.phone.trim() ? `${dial} ${form.phone.trim()}` : "";
      const authHeader = studentUser ? { Authorization: `Bearer ${localStorage.getItem("abc_student_token") || ""}` } : {};
      const { data: order } = await axios.post(`${API}/api/payments/create-order`, {
        batch_id: batch.id,
        type: effType,
        classes: effType === "dropin" ? classes : [],
        region,
        name: form.name.trim(),
        phone: combinedPhone,
        email: form.email.trim(),
      }, { headers: authHeader });

      await new Promise((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          order_id: order.order_id,
          name: "ABC Guitar Lessons",
          description: `${order.course_name} · ${order.batch_name}`,
          image: "/favicon.ico",
          prefill: order.prefill,
          theme: { color: "#DB0909" },
          notes: { batch_name: order.batch_name },
          modal: {
            ondismiss: () => {
              setSubmitting(false);
              setError("Payment cancelled. Your seat isn't held — try again when you're ready.");
              reject(new Error("dismissed"));
            },
          },
          handler: async (rp) => {
            try {
              const { data } = await axios.post(`${API}/api/payments/verify`, {
                razorpay_order_id: rp.razorpay_order_id,
                razorpay_payment_id: rp.razorpay_payment_id,
                razorpay_signature: rp.razorpay_signature,
              });
              setResult(data);
              resolve();
            } catch (verr) {
              const vd = verr.response?.data?.detail;
              setError(typeof vd === "string" ? vd : "Payment verified but reservation failed. Contact support with your payment ID.");
              reject(verr);
            } finally {
              setSubmitting(false);
            }
          },
        });
        rzp.on("payment.failed", (resp) => {
          setSubmitting(false);
          setError(resp?.error?.description || "Payment failed. Please try again.");
          reject(new Error("payment failed"));
        });
        rzp.open();
      });
    } catch (e) {
      if (e?.message !== "dismissed" && e?.message !== "payment failed") {
        const d = e.response?.data?.detail;
        if (!error) setError(typeof d === "string" ? d : (e.message || "Something went wrong. Please try again."));
      }
      setSubmitting(false);
    }
  };

  const fmtRes = (r) => fmtMoney(r.amount, r.currency === "USD" ? "intl" : "in");

  const waText = result
    ? encodeURIComponent(`Hi Sanjith! I just reserved a spot in ${result.batch_name} for "${result.course_name}" (${fmtRes(result)}). Looking forward to it!`)
    : "";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-32 md:py-48 overflow-hidden">
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 50% 45% at 30% 20%, rgba(219,9,9,0.06), transparent 65%)" }} />
      <div className="relative max-w-[1600px] mx-auto px-6 md:px-12">
        <SectionHeader number="06" kicker="Ready to begin">
          <h2 className="font-display font-black text-5xl sm:text-6xl lg:text-8xl leading-[0.95] text-[#1c1610]">
            <FadeUp>Let's <em className="italic text-[#DB0909]">play.</em></FadeUp>
          </h2>
        </SectionHeader>
        <FadeUp delay={0.1} className="mt-8 max-w-md">
          <p className="text-[#4a3e30] font-light leading-relaxed">
            Grab a seat in the next group batch — 10 spots each, twice a week, one month of music you'll love. Missed the start? Individual classes in the Ultimate course can be added on at {STATIC_PRICES[region].dropin}/class.
          </p>
        </FadeUp>

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <FadeUp className="lg:col-span-7">
            <div data-testid="booking-widget" className="border border-[rgba(28,22,16,0.14)] bg-[#fefefc] p-8 md:p-12">
              {result ? (
                <div data-testid="booking-success" className="text-center py-8">
                  <CheckCircle2 size={44} className="mx-auto text-[#DB0909]" />
                  <h3 className="mt-5 font-display font-bold text-2xl text-[#1c1610]">Payment received — you're in!</h3>
                  <p className="mt-3 text-[#4a3e30] font-light">
                    {result.course_name} · {result.batch_name} · {fmtRes(result)}
                  </p>
                  {result.payment_id && (
                    <p className="mt-2 font-mono text-[10px] tracking-[0.15em] uppercase text-[#4a3e30]">
                      Payment ID: <span className="text-[#1c1610]">{result.payment_id}</span>
                    </p>
                  )}
                  <p className="mt-1 font-mono text-[10px] tracking-[0.2em] uppercase text-[#950606]">
                    {result.spots_left} spots left · receipt sent to your email
                  </p>
                  <a
                    data-testid="booking-whatsapp-cta"
                    href={`${CONTACT.whatsapp}?text=${waText}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vibe mt-8 inline-flex items-center gap-3 font-mono text-xs tracking-[0.25em] uppercase bg-[#1c1610] text-[#f7f6f2] px-8 py-4 hover:bg-[#DB0909] active:bg-[#950606] transition-colors duration-300"
                  >
                    <MessageCircle size={16} /> Say hi on WhatsApp →
                  </a>
                  <button
                    data-testid="booking-again-btn"
                    onClick={resetBooking}
                    className="mt-4 block mx-auto font-mono text-[10px] tracking-[0.25em] uppercase text-[#950606] underline underline-offset-4 hover:text-[#DB0909] transition-colors duration-300 px-6 py-3"
                  >
                    ← Book another spot
                  </button>
                  <ShareCard result={result} />

                  {!studentUser && (
                    <div
                      data-testid="post-payment-signup-prompt"
                      className="mt-10 border border-[#DB0909]/40 bg-[#DB0909]/[0.04] p-5 md:p-6 text-left"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#DB0909]/15 border border-[#DB0909]/40 flex items-center justify-center text-[#DB0909] shrink-0">
                          <UserPlus size={15} />
                        </div>
                        <div className="flex-1">
                          <p className="font-display font-bold text-base text-[#1c1610]">Create your student profile</p>
                          <p className="mt-1 text-xs text-[#4a3e30] font-light leading-relaxed">
                            We'll upload your course materials, class schedule, and announcements to your profile. Takes 20 seconds — your booking will auto-link.
                          </p>
                          <Link
                            data-testid="post-payment-signup-link"
                            to={`/login?tab=register&email=${encodeURIComponent(form.email || "")}`}
                            className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase bg-[#1c1610] text-[#f7f6f2] px-5 py-2.5 hover:bg-[#DB0909] transition-colors"
                          >
                            <UserPlus size={12} /> Create account →
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Form heading */}
                  <div className="mb-8" data-testid="booking-form-header">
                    <h3 className="font-display font-black text-2xl sm:text-3xl text-[#1c1610] leading-none">Your booking</h3>
                    <p className="mt-2 text-sm text-[#4a3e30] font-light">Pick a course, month, and drop your details.</p>
                  </div>

                  <p className="font-display font-bold text-base text-[#1c1610] flex items-baseline gap-2">
                    <span className="font-mono text-[10px] tracking-[0.25em] text-[#DB0909]">01</span>
                    Choose your course
                  </p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    {[...COURSES].sort((a) => (a.id === "songs" ? -1 : 1)).map((c) => (
                      <Chip key={c.id} testId={`booking-course-${c.id}`} selected={course === c.id} onClick={() => setCourse(c.id)} className="flex-1">
                        <span className="block font-medium text-sm font-body">{c.name}</span>
                        <span className="block mt-1 text-[#DB0909]">{STATIC_PRICES[region][c.id]} · group batch</span>
                        {c.id === "indie" && (
                          <span className="block mt-1 normal-case tracking-normal text-[10px] text-[#4a3e30]">
                            Add-on: individual classes @ {STATIC_PRICES[region].dropin}/class once the batch starts
                          </span>
                        )}
                      </Chip>
                    ))}
                  </div>

                  <p className="mt-8 font-display font-bold text-base text-[#1c1610] flex items-baseline gap-2">
                    <span className="font-mono text-[10px] tracking-[0.25em] text-[#DB0909]">02</span>
                    Pick your month
                  </p>
                  <div className="mt-4 flex gap-3 overflow-x-auto pb-2 snap-x sm:grid sm:grid-cols-3 sm:overflow-visible sm:pb-0" data-testid="booking-batch-list">
                    {courseBatches.length === 0 && (
                      <p data-testid="booking-no-batch" className="col-span-full text-sm text-[#4a3e30] font-light">
                        All batches are full right now — ping us on WhatsApp and we'll save you a seat in the next one.
                      </p>
                    )}
                    {courseBatches.map((b) => {
                      const d = new Date(b.start_date + "T00:00:00");
                      const mon = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"][d.getMonth()];
                      const sel = batch?.id === b.id;
                      const batchNo = (b.name.match(/Batch\s+(\d+)/i) || [])[1];
                      return (
                        <button
                          key={b.id}
                          data-testid={`booking-batch-${b.course_id}-${mon.toLowerCase()}${batchNo ? `-b${batchNo}` : ""}`}
                          onClick={() => { setBatchId(b.id); setClasses([]); }}
                          className={`relative text-left px-4 py-4 border transition-colors duration-300 shrink-0 w-[46vw] max-w-[210px] snap-start sm:w-auto sm:max-w-none ${
                            sel ? "border-[#DB0909] bg-[#210101] text-[#f7f6f2]" : "border-[rgba(28,22,16,0.18)] text-[#1c1610] hover:border-[#DB0909]/60"
                          }`}
                        >
                          <span className={`absolute top-2 right-3 w-2 h-2 rounded-full ${b.status === "in_progress" ? "bg-[#DB0909] animate-pulse" : "bg-transparent"}`} />
                          {batchNo && (
                            <span
                              data-testid={`booking-batch-badge-${b.id}`}
                              className={`inline-block mb-2 px-2 py-[3px] font-mono text-[10px] font-bold tracking-[0.2em] uppercase rounded-sm ${
                                sel ? "bg-[#f7f6f2] text-[#DB0909]" : "bg-[#DB0909] text-[#f7f6f2]"
                              }`}
                            >
                              Batch {batchNo}
                            </span>
                          )}
                          <span className="block font-display font-black text-2xl leading-none">
                            {mon}<span className="ml-1 align-top text-[10px] font-mono font-normal opacity-60">'{String(d.getFullYear()).slice(2)}</span>
                          </span>
                          <span className={`block mt-2 font-mono text-[9px] tracking-[0.15em] uppercase ${sel ? "text-[#f7f6f2]/70" : "text-[#4a3e30]"}`}>
                            Starts {fmtDate(b.start_date)} · {b.class_days.map((day) => day.slice(0,3)).join("/")}
                          </span>
                          <span className="block mt-1 font-mono text-[11px] font-bold text-[#DB0909]">{b.spots_left}/{b.seats_total} spots left</span>
                          {b.status === "in_progress" && (
                            <span className={`block mt-1 font-mono text-[9px] tracking-[0.1em] uppercase ${sel ? "text-[#f7f6f2]/70" : "text-[#4a3e30]"}`}>
                              {b.classes_completed}/{b.total_classes} done · drop-in open
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {batch && batch.dropin_available && (
                    <div data-testid="dropin-callout" className="mt-8 border-2 border-[#DB0909] bg-[#DB0909]/[0.05] p-5 md:p-6">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <p className="font-display font-bold text-base text-[#1c1610] flex items-baseline gap-2">
                          <span className="font-mono text-[10px] tracking-[0.25em] text-[#DB0909]">03</span>
                          Drop-in classes
                        </p>
                        <p data-testid="dropin-remaining-count" className="font-display font-black text-xl md:text-2xl text-[#950606]">
                          {remaining.length} of {batch.total_classes} classes remaining
                        </p>
                      </div>
                      <p className="mt-2 text-sm text-[#1c1610] font-light">
                        This batch has already started — you can still join any remaining class below for <span className="font-medium text-[#950606]">{STATIC_PRICES[region].dropin} each</span>.
                      </p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {remaining.map(({ s, i }) => (
                          <Chip
                            key={i}
                            testId={`booking-class-${i}`}
                            selected={classes.includes(i)}
                            onClick={() => setClasses((cur) => (cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i]))}
                          >
                            {String(i + 1).padStart(2, "0")} · {s}
                          </Chip>
                        ))}
                      </div>
                      {classes.length > 0 && (
                        <p data-testid="dropin-selection-summary" className="mt-3 font-mono text-[11px] tracking-[0.15em] uppercase text-[#950606]">
                          {classes.length} class{classes.length > 1 ? "es" : ""} selected → {fmtMoney(classes.length * batchDropinPrice(batch, region), region)}
                        </p>
                      )}
                    </div>
                  )}

                  <p className="mt-8 font-display font-bold text-base text-[#1c1610] flex items-baseline gap-2">
                    <span className="font-mono text-[10px] tracking-[0.25em] text-[#DB0909]">{batch && batch.dropin_available ? "04" : "03"}</span>
                    Your details
                  </p>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input data-testid="booking-name-input" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-transparent border border-[rgba(28,22,16,0.18)] px-4 py-3 text-sm focus:outline-none focus:border-[#DB0909] placeholder:text-[#4a3e30]/60" />
                    <div className="flex items-stretch border border-[rgba(28,22,16,0.18)] focus-within:border-[#DB0909]">
                      <select
                        data-testid="booking-dial-select"
                        value={dial}
                        onChange={(e) => setDial(e.target.value)}
                        className="bg-transparent px-2 text-sm focus:outline-none border-r border-[rgba(28,22,16,0.14)] max-w-[90px]"
                        aria-label="Country dial code"
                      >
                        {COUNTRY_CODES.map((c) => (
                          <option key={c.c} value={c.dial}>{c.flag} {c.dial}</option>
                        ))}
                      </select>
                      <input
                        data-testid="booking-phone-input"
                        placeholder="Phone (optional)"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d\s-]/g, "") })}
                        className="flex-1 bg-transparent px-3 py-3 text-sm focus:outline-none placeholder:text-[#4a3e30]/60 min-w-0"
                      />
                    </div>
                  </div>
                  <div className="mt-3 relative">
                    <input
                      data-testid="booking-email-input"
                      placeholder="Email (required)"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      readOnly={!!studentUser}
                      className={`w-full bg-transparent border px-4 py-3 pr-32 text-sm focus:outline-none placeholder:text-[#4a3e30]/60 ${
                        studentUser
                          ? "border-[#16a34a]/70 text-[#1c1610] bg-[#16a34a]/[0.05] cursor-not-allowed"
                          : otp.verified
                            ? "border-[#16a34a]/70"
                            : "border-[rgba(28,22,16,0.18)] focus:border-[#DB0909]"
                      }`}
                    />
                    {(studentUser || otp.verified) && (
                      <span data-testid="email-verified-badge" className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.15em] uppercase text-[#16a34a]">
                        <CheckCircle2 size={12} /> {studentUser ? "Signed in" : "Verified"}
                      </span>
                    )}
                    {!studentUser && emailValid && !otp.verified && (
                      <button
                        type="button"
                        data-testid="booking-send-otp-btn"
                        onClick={sendOtp}
                        disabled={otp.sending || otp.cooldown > 0}
                        className="absolute right-2 top-1/2 -translate-y-1/2 font-mono text-[10px] tracking-[0.15em] uppercase px-3 py-1.5 border border-[#DB0909]/50 text-[#950606] hover:bg-[#DB0909] hover:text-[#f7f6f2] transition-colors duration-200 disabled:opacity-50"
                      >
                        {otp.sending ? "Sending…" : otp.cooldown > 0 ? `Resend ${otp.cooldown}s` : otp.sent ? "Resend" : "Verify email"}
                      </button>
                    )}
                  </div>

                  {/* OTP slide-in */}
                  <div
                    data-testid="booking-otp-panel"
                    className={`overflow-hidden transition-all duration-500 ease-out ${
                      !studentUser && otp.open && !otp.verified ? "max-h-40 mt-3 opacity-100" : "max-h-0 opacity-0"
                    }`}
                    aria-hidden={!!studentUser || !otp.open || otp.verified}
                  >
                    <div className="border border-[#DB0909]/40 bg-[#DB0909]/[0.03] px-4 py-3 flex flex-wrap items-center gap-3">
                      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#950606]">Enter code from email</span>
                      <input
                        data-testid="booking-otp-input"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        pattern="[0-9]*"
                        maxLength={6}
                        placeholder="••••••"
                        value={otp.code}
                        onChange={(e) => setOtp((o) => ({ ...o, code: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                        onKeyDown={(e) => { if (e.key === "Enter") verifyOtp(); }}
                        className="bg-transparent border-b-2 border-[#DB0909]/50 focus:outline-none focus:border-[#DB0909] font-mono tracking-[0.4em] text-center text-lg w-32 py-1"
                      />
                      <button
                        type="button"
                        data-testid="booking-verify-otp-btn"
                        onClick={verifyOtp}
                        disabled={otp.verifying || otp.code.length < 4}
                        className="font-mono text-[10px] tracking-[0.15em] uppercase px-3 py-1.5 bg-[#1c1610] text-[#f7f6f2] hover:bg-[#DB0909] transition-colors disabled:opacity-50"
                      >
                        {otp.verifying ? "Verifying…" : "Verify"}
                      </button>
                      {otp.msg && <span className="font-mono text-[10px] text-[#4a3e30] flex-1">{otp.msg}</span>}
                    </div>
                  </div>
                  {!studentUser && emailHints(form.email).length > 0 && (
                    <div data-testid="email-suggestions" className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#4a3e30]">Did you mean:</span>
                      {emailHints(form.email).map((s) => (
                        <button
                          key={s}
                          data-testid={`email-suggestion-${s.split("@")[1]}`}
                          onClick={() => setForm({ ...form, email: s })}
                          className="font-mono text-[11px] border border-[#DB0909]/40 text-[#950606] px-3 py-1 hover:bg-[#DB0909] hover:text-[#f7f6f2] transition-colors duration-200"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}

                  {error && <p data-testid="booking-error" className="mt-4 text-sm text-[#DB0909]">{error}</p>}

                  <button
                    data-testid="booking-submit-btn"
                    disabled={submitting || !batch}
                    onMouseEnter={() => pluck(196)}
                    onClick={submit}
                    className="vibe mt-8 flex items-center justify-center gap-3 w-full font-mono text-xs tracking-[0.25em] uppercase bg-[#1c1610] text-[#f7f6f2] py-5 hover:bg-[#DB0909] active:bg-[#950606] transition-colors duration-300 disabled:opacity-50"
                  >
                    {submitting
                      ? "Opening secure checkout…"
                      : (studentUser || otp.verified)
                        ? `Continue to payment${amount ? ` → ${fmtMoney(amount, region)}` : ""}`
                        : "Verify email & continue"}
                  </button>
                  <p className="mt-3 font-mono text-[10px] tracking-[0.2em] uppercase text-[#4a3e30] text-center">
                    Secure checkout by Razorpay · Cards · UPI · Netbanking
                  </p>
                </>
              )}
            </div>
          </FadeUp>

          <div className="lg:col-span-5 flex flex-col gap-6">
            <FadeUp delay={0.1}>
              <a data-testid="contact-phone-card" href={`tel:${CONTACT.phoneRaw}`} className="group block border border-[rgba(28,22,16,0.14)] bg-[#fefefc] p-8 hover:border-[#DB0909]/50 transition-colors duration-500">
                <Phone size={18} className="text-[#950606]" />
                <p className="mt-5 font-mono text-[10px] tracking-[0.3em] uppercase text-[#4a3e30]">Call / WhatsApp</p>
                <p className="mt-2 font-display font-bold text-2xl md:text-3xl text-[#1c1610] group-hover:text-[#950606] transition-colors duration-300">{CONTACT.phone}</p>
                <p className="mt-2 text-sm text-[#4a3e30] font-light">Sanjith · Available daily</p>
              </a>
            </FadeUp>
            <FadeUp delay={0.2}>
              <a data-testid="contact-email-card" href={`mailto:${CONTACT.email}`} className="group block border border-[rgba(28,22,16,0.14)] bg-[#fefefc] p-8 hover:border-[#DB0909]/50 transition-colors duration-500">
                <Mail size={18} className="text-[#950606]" />
                <p className="mt-5 font-mono text-[10px] tracking-[0.3em] uppercase text-[#4a3e30]">Email</p>
                <p className="mt-2 font-display font-bold text-xl md:text-2xl text-[#1c1610] group-hover:text-[#950606] transition-colors duration-300 break-all">{CONTACT.email}</p>
                <p className="mt-2 text-sm text-[#4a3e30] font-light">Response within 24 hours</p>
              </a>
            </FadeUp>
            <FadeUp delay={0.3}>
              <div className="border border-[rgba(28,22,16,0.14)] p-8">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#4a3e30]">Classes available in</p>
                <p className="mt-3 font-display italic text-xl text-[#1c1610]">{LANGUAGES.join(" · ")}</p>
              </div>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
};
